import { useEffect, useLayoutEffect, useRef, useState, type TouchEvent } from 'react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react';
import CloseIcon from '@/assets/icons/close-x.svg?react';
import CancelCaptionIcon from '@/assets/icons/photo-caption-cancel.svg?react';
import SaveCaptionIcon from '@/assets/icons/photo-caption-check.svg?react';
import EditIcon from '@/assets/icons/photo-edit.svg?react';
import TrashIcon from '@/assets/icons/photo-trash.svg?react';
import styles from './PhotoCarousel.module.scss';

export interface PhotoCarouselItem {
  id: string | number;
  src: string;
  label?: string;
  alt?: string;
  updatedAt?: string | Date | number;
  typeLabel?: string;
  storageObjectId?: number;
  uploadStatus?: 'local' | 'processing' | 'complete' | 'error';
}

interface PhotoCarouselProps {
  isOpen: boolean;
  photos: PhotoCarouselItem[];
  initialPhotoId?: string | number;
  initialIndex?: number;
  className?: string;
  onClose: () => void;
  /* eslint-disable no-unused-vars */
  onEdit?: (photo: PhotoCarouselItem, caption: string) => void | Promise<void>;
  onDelete?: (photo: PhotoCarouselItem) => void;
  onChange?: (photo: PhotoCarouselItem, index: number) => void;
  /* eslint-enable no-unused-vars */
}

function PhotoCarousel({ isOpen, photos, initialPhotoId, initialIndex = 0, className = '', onClose, onEdit, onDelete, onChange }: PhotoCarouselProps) {
  const touchStartPoint = useRef<{ x: number; y: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [editingPhotoId, setEditingPhotoId] = useState<string | number>();
  const [captionDraft, setCaptionDraft] = useState('');
  const [isSavingCaption, setIsSavingCaption] = useState(false);
  const initialPhotoIndex = initialPhotoId === undefined ? -1 : photos.findIndex((photo) => photo.id === initialPhotoId);
  const activePhoto = photos[activeIndex];
  const isEditingCaption = Boolean(activePhoto && editingPhotoId === activePhoto.id);
  const hasPreviousPhoto = activeIndex > 0;
  const hasNextPhoto = activeIndex < photos.length - 1;

  useLayoutEffect(() => {
    if (!isOpen) return;

    const nextIndex = initialPhotoIndex >= 0 ? initialPhotoIndex : Math.min(Math.max(initialIndex, 0), Math.max(photos.length - 1, 0));

    setActiveIndex((currentIndex) => (currentIndex === nextIndex ? currentIndex : nextIndex));
  }, [initialIndex, initialPhotoIndex, isOpen, photos.length]);

  useEffect(() => {
    if (!isOpen || !activePhoto || !editingPhotoId || editingPhotoId === activePhoto.id) return;
    setEditingPhotoId(undefined);
    setCaptionDraft('');
  }, [activePhoto, editingPhotoId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const isCaptionInput = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;
      if (isCaptionInput) return;

      if (!['Escape', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;

      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') showPreviousPhoto();
      if (event.key === 'ArrowRight') showNextPhoto();
    };

    const originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  });

  const setActivePhotoIndex = (nextIndex: number) => {
    setActiveIndex((currentIndex) => {
      if (currentIndex === nextIndex) {
        return currentIndex;
      }

      const nextPhoto = photos[nextIndex];
      if (nextPhoto) {
        onChange?.(nextPhoto, nextIndex);
      }

      return nextIndex;
    });
  };

  const showPreviousPhoto = () => {
    setActivePhotoIndex(Math.max(activeIndex - 1, 0));
  };

  const showNextPhoto = () => {
    setActivePhotoIndex(Math.min(activeIndex + 1, photos.length - 1));
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    touchStartPoint.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const touchStart = touchStartPoint.current;
    const touch = event.changedTouches[0];
    touchStartPoint.current = null;

    if (!touchStart || !touch) return;

    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const isHorizontalSwipe = Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY);

    if (!isHorizontalSwipe) return;
    if (deltaX > 0) showPreviousPhoto();
    if (deltaX < 0) showNextPhoto();
  };

  const startCaptionEdit = () => {
    if (!activePhoto || isSavingCaption) return;
    setEditingPhotoId(activePhoto.id);
    setCaptionDraft(activePhoto.label ?? '');
  };

  const cancelCaptionEdit = () => {
    if (isSavingCaption) return;
    setEditingPhotoId(undefined);
    setCaptionDraft('');
  };

  const saveCaptionEdit = async () => {
    if (!activePhoto || !onEdit || isSavingCaption) return;
    setIsSavingCaption(true);
    cancelCaptionEdit();

    try {
      await onEdit(activePhoto, captionDraft.trim() || activePhoto.label || '');
    } finally {
      setIsSavingCaption(false);
    }
  };

  if (!isOpen || !activePhoto) return null;

  const showCaptionInput = isEditingCaption;
  const showCaptionLabel = !isEditingCaption;
  const isPersistReady = Boolean(activePhoto.storageObjectId && activePhoto.uploadStatus === 'complete');
  const showActions = isEditingCaption || (isPersistReady && (onEdit || onDelete));
  const showSavingCaptionIndicator = isSavingCaption;
  const showEditAction = !isEditingCaption && isPersistReady && onEdit;
  const showDeleteAction = !isEditingCaption && isPersistReady && onDelete && !isSavingCaption;
  const showCaptionEditActions = isEditingCaption;
  const showTypeLabel = Boolean(activePhoto.typeLabel);

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <section className={`${styles.carousel} ${className}`} role="dialog" aria-modal="true" aria-label="Photo carousel" onClick={(event) => event.stopPropagation()}>
        <button type="button" className={`${styles.iconButton} ${styles.closeButton}`} aria-label="Close photo carousel" onClick={onClose}>
          <CloseIcon aria-hidden="true" />
        </button>

        <div className={styles.imageFrame} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {showTypeLabel && <div className={styles.typeLabel}>{activePhoto.typeLabel}</div>}

          {hasPreviousPhoto && (
            <button type="button" className={`${styles.iconButton} ${styles.previousButton}`} aria-label="View previous photo" onClick={showPreviousPhoto}>
              <ArrowLeftIcon aria-hidden="true" />
            </button>
          )}
          {hasNextPhoto && (
            <button type="button" className={`${styles.iconButton} ${styles.nextButton}`} aria-label="View next photo" onClick={showNextPhoto}>
              <ArrowRightIcon aria-hidden="true" />
            </button>
          )}
          <img className={styles.image} src={activePhoto.src} alt={activePhoto.alt ?? activePhoto.label ?? 'Selected photo'} />
        </div>

        <footer className={styles.footer}>
          {showCaptionInput && (
            <input
              className={styles.captionInput}
              value={captionDraft}
              onChange={(event) => setCaptionDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') saveCaptionEdit();
                if (event.key === 'Escape') cancelCaptionEdit();
              }}
              autoFocus
            />
          )}

          {showCaptionLabel && (
            <div className={styles.details}>
              <div className={styles.label}>{activePhoto.label}</div>
            </div>
          )}

          {showActions && (
            <div className={styles.actions}>
              {showSavingCaptionIndicator && (
                <span className={styles.captionSavingIndicator} role="status" aria-label="Saving caption">
                  <span className={styles.captionSavingSpinner} aria-hidden="true" />
                </span>
              )}

              {showCaptionEditActions && (
                <>
                  <button type="button" className={styles.actionButton} aria-label="Save caption" onClick={saveCaptionEdit} disabled={isSavingCaption}>
                    <SaveCaptionIcon aria-hidden="true" />
                  </button>
                  <button type="button" className={styles.actionButton} aria-label="Cancel caption edit" onClick={cancelCaptionEdit} disabled={isSavingCaption}>
                    <CancelCaptionIcon aria-hidden="true" />
                  </button>
                </>
              )}

              {showEditAction && (
                <button type="button" className={`${styles.actionButton} ${styles.editIcon}`} aria-label="Edit photo caption" onClick={startCaptionEdit} disabled={isSavingCaption}>
                  <EditIcon aria-hidden="true" />
                </button>
              )}

              {showDeleteAction && (
                <button type="button" className={`${styles.actionButton} ${styles.trashIcon}`} aria-label="Delete photo" onClick={() => onDelete(activePhoto)}>
                  <TrashIcon aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </footer>
      </section>
    </div>
  );
}

export default PhotoCarousel;
